import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import { Alert, Button, Card, Input, Segmented, Spin, Table, Tag, Tooltip, message } from 'antd';
import {
  CustomerServiceOutlined,
  DownloadOutlined,
  PlusOutlined,
  ProjectOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import useColumnConfig, { getStoredPageSize } from '../hooks/useColumnConfig';
import ColumnConfigDrawer from '../components/ColumnConfigDrawer';
import TableFilterDrawer, { TableFilterButton, countActiveFilters } from '../components/TableFilterDrawer';
import CreateTicketModal from '../components/CreateTicketModal';
import TicketsBoard from '../components/TicketsBoard';
import PartnerLogo from '../components/PartnerLogo';
import { getPartners } from '../modules/partners/api';
import {
  getSupportTickets,
  TICKET_CATEGORIES,
  TICKET_STATUSES,
  TICKET_STATUS_COLORS,
} from '../modules/support-tickets/api';
import { buildCsv, downloadCsv, escapeCsvField } from '../utils/csv';

const fmtTime = (iso) => (iso ? dayjs(iso).format('DD MMM YYYY HH:mm') : '—');

const ROLE_LABELS = {
  ADMIN: 'Ops',
  USER: 'Ops',
  PARTNER_VIEWER: 'Partner',
  DRIVER: 'Driver',
  SYSTEM: 'Auto',
};

/**
 * OVO-117 — Monitoring → Tickets. The support triage list: OPEN first, then
 * most recent activity. Same house table stack as the other Monitoring pages
 * (Settings gear owns columns + page size, filter drawer is server-side).
 */
// #132 — link filters carried in the URL (the box page "See all", the Caisses
// ticket counts) + the resolved window (default: only the last 30 days of
// RESOLVED / WONT_FIX, so old ones stop cluttering the list).
const LINK_KEYS = ['collectionPointId', 'siteId', 'orderScheduleId'];
const RESOLVED_WINDOW_DAYS = 30;

const TicketsPage = () => {
  const { t } = useTranslation(['routes', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'board' ? 'board' : 'list';
  const resolvedAll = searchParams.get('resolved') === 'all';
  const linkKey = LINK_KEYS.map((k) => searchParams.get(k) || '').join('|');
  const linkFilter = Object.fromEntries(
    LINK_KEYS.filter((k) => searchParams.get(k)).map((k) => [k, searchParams.get(k)]),
  );
  const urlParams = {
    ...linkFilter,
    ...(resolvedAll ? {} : { resolvedWithinDays: RESOLVED_WINDOW_DAYS }),
  };
  // Merge into the URL instead of replacing it (view / link / resolved coexist).
  const patchSearch = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => (v == null || v === '' ? next.delete(k) : next.set(k, v)));
    setSearchParams(next);
  };
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => getStoredPageSize('tickets-columns', 50));
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [q, setQ] = useState('');
  const [partnerOptions, setPartnerOptions] = useState([]);
  // Opened directly with creation context (e.g. Inconsistencies row action).
  const [createOpen, setCreateOpen] = useState(!!location.state?.create);
  const [messageApi, contextHolder] = message.useMessage();

  const load = useCallback(
    async (p = page, ps = pageSize, f = filters, search = q) => {
      setLoading(true);
      try {
        const { response } = await getSupportTickets({
          take: ps,
          skip: (p - 1) * ps,
          ...(search ? { q: search } : {}),
          ...urlParams,
          ...f,
        });
        setRows(response?.items || []);
        setTotal(response?.total || 0);
      } catch (e) {
        messageApi.error(e?.message || t('common:error'));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, pageSize, filters, q, linkKey, resolvedAll],
  );

  // Reload when the URL link filter / resolved window changes.
  const firstUrlLoad = React.useRef(true);
  useEffect(() => {
    if (firstUrlLoad.current) {
      firstUrlLoad.current = false;
      return;
    }
    setPage(1);
    load(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkKey, resolvedAll]);

  useEffect(() => {
    load(1, pageSize);
    getPartners({ take: 300 }).then(({ response }) => {
      setPartnerOptions((response?.partners || []).map((p) => ({ value: p.id, label: p.name })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusTag = (s) => <Tag color={TICKET_STATUS_COLORS[s] || 'default'}>{t(`tickets.statuses.${s}`, s)}</Tag>;

  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id) => <Link to={`/admin/tickets/${id}`}>#{id}</Link>,
    },
    {
      title: t('tickets.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: statusTag,
    },
    {
      title: t('tickets.category'),
      dataIndex: 'category',
      key: 'category',
      width: 170,
      render: (c) => <Tag>{t(`tickets.categories.${c}`, c)}</Tag>,
    },
    {
      title: t('tickets.subject'),
      dataIndex: 'subject',
      key: 'subject',
      render: (v, rec) => (
        <span>
          {rec.internal && (
            <Tooltip title={t('tickets.internalHint')}>
              <Tag color="purple" style={{ marginRight: 6 }}>
                {t('tickets.internalTag')}
              </Tag>
            </Tooltip>
          )}
          {v}
        </span>
      ),
    },
    {
      title: t('tickets.partner'),
      key: 'partner',
      width: 150,
      render: (_, rec) =>
        rec.partner ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <PartnerLogo name={rec.partner.name} logoKey={rec.partner.logoFile?.key} size={18} />
            {rec.partner.name}
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: t('tickets.order'),
      key: 'order',
      width: 130,
      render: (_, rec) =>
        rec.order?.orderCode ? (
          <Link to={`/admin/orders/${rec.order.orderCode}`}>{rec.order.orderCode}</Link>
        ) : (
          rec.orderCode || '—'
        ),
    },
    {
      title: t('tickets.where'),
      key: 'where',
      width: 170,
      render: (_, rec) =>
        rec.collectionPoint ? (
          <Link to={`/admin/collection-points/${rec.collectionPoint.id}`}>
            {rec.collectionPoint.externalRef || rec.collectionPoint.name}
          </Link>
        ) : rec.site ? (
          rec.site.name
        ) : rec.orderSchedule ? (
          t('tickets.schedule', { id: rec.orderSchedule.id })
        ) : (
          '—'
        ),
    },
    {
      title: t('tickets.reporter'),
      key: 'reporter',
      width: 150,
      render: (_, rec) => (
        <span>
          <Tag>{ROLE_LABELS[rec.creatorRole] || rec.creatorRole}</Tag>
          {rec.createdBy?.name || ''}
        </span>
      ),
    },
    {
      title: t('tickets.assignee'),
      key: 'assignee',
      width: 130,
      render: (_, rec) => rec.assignee?.name || '—',
    },
    {
      title: t('tickets.lastActivity'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      render: fmtTime,
    },
  ];

  const exportCsv = () => {
    const header = [
      'Id',
      'Status',
      'Category',
      'Subject',
      'Internal',
      'Partner',
      'Order',
      'Box',
      'Site',
      'Reporter role',
      'Reporter',
      'Assignee',
      'Created',
      'Last activity',
    ];
    const lines = rows.map((r) => [
      r.id,
      r.status,
      r.category,
      r.subject,
      r.internal ? 'yes' : 'no',
      r.partner?.name || '',
      r.order?.orderCode || r.orderCode || '',
      r.collectionPoint ? r.collectionPoint.externalRef || r.collectionPoint.name : '',
      r.site?.name || '',
      r.creatorRole,
      r.createdBy?.name || '',
      r.assignee?.name || '',
      fmtTime(r.createdAt),
      fmtTime(r.updatedAt),
    ]);
    downloadCsv(
      buildCsv([header, ...lines].map((row) => row.map(escapeCsvField).join(','))),
      `tickets-${dayjs().format('YYYY-MM-DD')}.csv`,
    );
  };

  const FIXED_KEYS = ['id'];
  const columnConfig = useColumnConfig('tickets-columns', columns, FIXED_KEYS);

  return (
    <div className="TicketsPage Content" style={{ paddingBottom: 24 }}>
      {contextHolder}
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>
              <CustomerServiceOutlined style={{ marginRight: 8 }} />
              {t('tickets.title')}
            </h2>
            <div style={{ color: 'var(--text-muted)', maxWidth: 720, marginTop: 4 }}>{t('tickets.subtitle')}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Segmented
              value={view}
              onChange={(v) => patchSearch({ view: v === 'board' ? 'board' : null })}
              options={[
                { value: 'list', label: t('tickets.viewList'), icon: <UnorderedListOutlined /> },
                { value: 'board', label: t('tickets.viewBoard'), icon: <ProjectOutlined /> },
              ]}
            />
            {view === 'list' && (
              <Segmented
                value={resolvedAll ? 'all' : 'recent'}
                onChange={(v) => patchSearch({ resolved: v === 'all' ? 'all' : null })}
                options={[
                  { value: 'recent', label: t('tickets.resolvedRecent') },
                  { value: 'all', label: t('tickets.resolvedAll') },
                ]}
              />
            )}
            <Input.Search
              allowClear
              style={{ width: 220 }}
              placeholder={t('tickets.searchPlaceholder')}
              onSearch={(value) => {
                setQ(value);
                setPage(1);
                load(1, pageSize, filters, value);
              }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              {t('tickets.newTicket')}
            </Button>
            {view === 'list' && (
              <Button icon={<DownloadOutlined />} onClick={exportCsv} disabled={rows.length === 0}>
                CSV
              </Button>
            )}
            {view === 'list' && (
              <Button icon={<ReloadOutlined />} loading={loading} onClick={() => load(page, pageSize)} />
            )}
            <TableFilterButton activeCount={countActiveFilters(filters)} onClick={() => setFilterOpen(true)} />
            {view === 'list' && (
              <Button
                type="text"
                title="Settings"
                onClick={columnConfig.openDrawer}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <ReactSVG className="icon" src="/icons/settings.svg" />
              </Button>
            )}
          </div>
        </div>

        {Object.keys(linkFilter).length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {Object.entries(linkFilter).map(([k, v]) => {
              const first = rows[0];
              const label =
                k === 'collectionPointId'
                  ? t('tickets.box', {
                      name: first?.collectionPoint?.externalRef || first?.collectionPoint?.name || `#${v}`,
                    })
                  : k === 'siteId'
                    ? t('tickets.site', { name: first?.site?.name || `#${v}` })
                    : t('tickets.schedule', { id: v });
              return (
                <Tag key={k} closable color="blue" onClose={() => patchSearch({ [k]: null })}>
                  {t('tickets.filteredBy', { label })}
                </Tag>
              );
            })}
          </div>
        )}
        {view === 'board' && <TicketsBoard filters={{ ...linkFilter, ...filters }} q={q} />}
        {view === 'list' && (
          <Spin spinning={loading}>
            {rows.length === 0 && !loading ? (
              <Alert type="success" showIcon message={t('tickets.empty')} />
            ) : (
              <Table
                rowKey="id"
                size="small"
                columns={columnConfig.visibleColumns}
                dataSource={rows}
                onRow={(rec) => ({
                  onClick: (e) => {
                    if (e.target.closest('a')) return;
                    navigate(`/admin/tickets/${rec.id}`);
                  },
                  style: { cursor: 'pointer' },
                })}
                pagination={{
                  current: page,
                  pageSize,
                  total,
                  showSizeChanger: false,
                  onChange: (p, ps) => {
                    setPage(p);
                    setPageSize(ps);
                    load(p, ps);
                  },
                }}
              />
            )}
          </Spin>
        )}

        <TableFilterDrawer
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          values={filters}
          onApply={(f) => {
            setFilters(f);
            setPage(1);
            load(1, pageSize, f);
          }}
          fields={[
            {
              name: 'status',
              label: t('tickets.status'),
              placeholder: '—',
              options: TICKET_STATUSES.map((s) => ({ value: s, label: t(`tickets.statuses.${s}`, s) })),
            },
            {
              name: 'category',
              label: t('tickets.category'),
              placeholder: '—',
              options: TICKET_CATEGORIES.map((c) => ({ value: c, label: t(`tickets.categories.${c}`, c) })),
            },
            { name: 'partnerId', label: t('tickets.partner'), options: partnerOptions, placeholder: '—' },
            {
              name: 'creatorRole',
              label: t('tickets.reporter'),
              placeholder: '—',
              options: ['ADMIN', 'USER', 'PARTNER_VIEWER', 'DRIVER', 'SYSTEM'].map((r) => ({
                value: r,
                label: ROLE_LABELS[r] || r,
              })),
            },
            { name: 'orderCode', label: t('tickets.order'), type: 'text', placeholder: 'PXXXXXXXX' },
          ]}
        />
        <ColumnConfigDrawer
          open={columnConfig.drawerOpen}
          onClose={columnConfig.closeDrawer}
          columns={columnConfig.orderedAllColumns}
          hidden={columnConfig.hidden}
          fixedKeys={FIXED_KEYS}
          onToggle={columnConfig.toggleColumn}
          onMove={columnConfig.moveColumn}
          onReset={columnConfig.resetConfig}
          pageSize={columnConfig.pageSize}
          onPageSizeChange={(size) => {
            columnConfig.setPageSize(size);
            setPageSize(size);
            setPage(1);
            load(1, size);
          }}
        />
        <CreateTicketModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={() => load(1, pageSize)}
          prefill={location.state?.create || {}}
        />
      </Card>
    </div>
  );
};

export default TicketsPage;
